import { useMemo, useState } from "react";
import { useLazyQuery, useQuery } from "@apollo/client";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import { InlineSpinner } from "components/base/Spinner";
import { FETCH_ALC_XML_DOWNLOAD, FETCH_ALC_XML_PREVIEW } from "./graphql";

const XmlPreviewModal = ({ xmlFileName, hide }) => {
  const [downloadError, setDownloadError] = useState("");
  const { data, loading, error } = useQuery(FETCH_ALC_XML_PREVIEW, {
    variables: { xml: xmlFileName },
    fetchPolicy: "network-only",
  });
  const [fetchXmlDownload, { loading: downloading }] = useLazyQuery(
    FETCH_ALC_XML_DOWNLOAD,
    {
      fetchPolicy: "network-only",
    }
  );

  const preview = useMemo(() => {
    if (!data?.alcXmlPreview) return null;
    try {
      return JSON.parse(data.alcXmlPreview);
    } catch (e) {
      console.error("Failed to parse alcXmlPreview", e);
      return null;
    }
  }, [data?.alcXmlPreview]);

  const handleDownload = async () => {
    setDownloadError("");
    try {
      const { data: downloadData } = await fetchXmlDownload({
        variables: { xml: xmlFileName },
      });
      if (!downloadData?.alcXmlDownload) {
        throw new Error("No XML content.");
      }

      const payload = JSON.parse(downloadData.alcXmlDownload);
      const fileName = payload.fileName || xmlFileName || "shipment.xml";
      const content = payload.content || "";

      const blob = new Blob([content], {
        type: "application/xml;charset=utf-8",
      });
      const blobUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = blobUrl;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      console.error("Failed to download XML file", e);
      setDownloadError("Failed to download XML file.");
    }
  };

  if (loading) {
    return (
      <div className="py-8 flex justify-center">
        <InlineSpinner text="Loading XML preview..." />
      </div>
    );
  }

  if (error) {
    return <Errors error={error} className="py-6" />;
  }

  if (!preview) {
    return <div className="opacity-70 py-6">No preview data.</div>;
  }

  return (
    <div className="flex flex-col h-[70vh] min-h-[320px]">
      <div className="text-xs opacity-70 mb-3">
        Lines: {preview.shownLines} / {preview.totalLines}
      </div>

      <div className="flex-1 min-h-0 overflow-auto border border-gray-200 rounded bg-gray-50 p-3">
        <pre className="whitespace-pre-wrap text-xs leading-relaxed">
          {preview.previewText || ""}
        </pre>
      </div>

      {downloadError ? (
        <div className="text-xs text-red-600 mt-3">{downloadError}</div>
      ) : null}

      <div className="mt-4 flex justify-end gap-2">
        <Button onClick={handleDownload} disabled={downloading}>
          {downloading ? "Downloading..." : "Download XML"}
        </Button>
        <Button onClick={hide}>Close</Button>
      </div>
    </div>
  );
};

export default XmlPreviewModal;
